/**
 * Role Guard Component
 *
 * Restricts access to components based on user role.
 * Shows forbidden message if user doesn't have required role.
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { Role } from '@functieloonhuis/shared';
import { useAuth } from './AuthContext';

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: Role | Role[];
  fallback?: ReactNode;
  redirectTo?: string;
  /**
   * Ook toelaten wanneer de gebruiker een AFGELEIDE bevoegdheid heeft die niet
   * in de rol-enum zit — "leidinggevende" is `departments.managerId`, geen rol
   * (AD-4, `teamGuard.ts`). Zonder deze klep zou een leidinggevende met rol
   * `medewerker` een teamscherm niet kunnen openen terwijl de server hem wél
   * bedient.
   *
   * Uitsluitend VERRUIMEND: `allowedRoles` blijft gewoon gelden.
   */
  allowTeamManager?: boolean;
}

export function RoleGuard({
  children,
  allowedRoles,
  fallback,
  redirectTo,
  allowTeamManager = false,
}: RoleGuardProps) {
  const { hasRole, isAuthenticated, isLoading, isTeamManagerOrHR } = useAuth();

  if (isLoading) {
    return (
      <div data-testid="loading-spinner" role="status" aria-live="polite">
        Laden...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasRole(allowedRoles) && !(allowTeamManager && isTeamManagerOrHR)) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        data-testid="forbidden-message"
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Geen toegang</h1>
          <p className="mt-2 text-gray-600">
            U heeft geen rechten om deze pagina te bekijken.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
